'use client';

import { useEffect, useRef } from 'react';

interface UseThreadPaginationSentinelOptions {
  enabled: boolean;
  isFetching: boolean;
  onLoadMore: () => void;
  /** Reconnect the observer when the loaded page or thread changes. */
  resetKey?: string | number;
  /** Reset automatic loading only when the list identity changes. */
  progressKey?: string | number;
  /** Require the sentinel to leave the lead area before another automatic load. */
  requireExitBeforeNextLoad?: boolean;
  rootMargin?: string;
}

/**
 * Observes a small footer at the end of a thread using the real page viewport.
 *
 * The lock is deliberately kept outside the observer callback. Intersection
 * observers can emit several entries while a request is being scheduled, and
 * a single page transition should result in one request at most. The lock is
 * released only after the state reports that the request is no longer fetching.
 */
export const useThreadPaginationSentinel = ({
  enabled,
  isFetching,
  onLoadMore,
  resetKey,
  progressKey,
  requireExitBeforeNextLoad = false,
  rootMargin = '400px 0px',
}: UseThreadPaginationSentinelOptions) => {
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const callbackRef = useRef(onLoadMore);
  const requestLockRef = useRef(false);
  const wasIntersectingRef = useRef(false);
  const previousProgressKeyRef = useRef(progressKey);
  const stateRef = useRef({ enabled, isFetching });

  useEffect(() => {
    callbackRef.current = onLoadMore;
    stateRef.current = { enabled, isFetching };
  }, [enabled, isFetching, onLoadMore]);

  useEffect(() => {
    if (!isFetching) {
      requestLockRef.current = false;
    }
  }, [isFetching]);

  useEffect(() => {
    if (!requireExitBeforeNextLoad) return;
    if (previousProgressKeyRef.current === progressKey) return;
    previousProgressKeyRef.current = progressKey;
    wasIntersectingRef.current = false;
    requestLockRef.current = false;
  }, [progressKey, requireExitBeforeNextLoad]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !enabled || typeof IntersectionObserver === 'undefined') return;

    requestLockRef.current = false;
    const observer = new IntersectionObserver(
      (entries) => {
        const isIntersecting = entries.some((entry) => entry.isIntersecting);
        if (!isIntersecting) {
          if (requireExitBeforeNextLoad) {
            wasIntersectingRef.current = false;
          }
          return;
        }

        // Reconnecting after each successful page can report an already-visible
        // sentinel again. Require it to leave the lead area before automatically
        // loading another page when the caller opts into this bounded mode.
        if (requireExitBeforeNextLoad && wasIntersectingRef.current) return;

        if (!stateRef.current.enabled || stateRef.current.isFetching || requestLockRef.current) {
          return;
        }

        requestLockRef.current = true;
        if (requireExitBeforeNextLoad) {
          wasIntersectingRef.current = true;
        }
        callbackRef.current();
      },
      { root: null, rootMargin, threshold: 0 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [enabled, progressKey, requireExitBeforeNextLoad, resetKey, rootMargin]);

  return sentinelRef;
};
